import { ReactNode } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, styled } from '@mui/material'
import { ExpandMore as ExpandMoreIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material'
import { StepIconEvent } from '../tracking/timeline'
import { resource } from '../../utils/utilities'
import DEFAULT from '../../config/default'

const RootStyled = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(5)
}))

const ListStyled = styled('ul')(({ theme }) => ({
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(3)
}))

const StatusRowStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    '&:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`
    }
}))

const StatusIconWrapperStyled = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '& > div': {
        flexShrink: 0,
        transform: 'scale(0.6)'
    }
}))

const WhatsAppCtaStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    width: 'fit-content',
    maxWidth: '100%',
    '&:hover': {
        background: theme.palette.grey[200]
    }
}))

const WhatsAppCtaIconStyled = styled(WhatsAppIcon)(() => ({
    color: '#25D366',
    width: 28,
    height: 28,
    flexShrink: 0
}))

const ScreenshotStyled = styled('img')(({ theme }) => ({
    display: 'block',
    width: '100%',
    maxWidth: 640,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
}))

interface OrderStatusItem {
    icon: string,
    label: string,
    description: string
}

const ORDER_STATUSES: Array<OrderStatusItem> = [
    { icon: '0-3', label: 'Aprobado', description: 'Recibimos y aprobamos tu compra. En breve comenzaremos a preparar tu pedido.' },
    { icon: '1-1', label: 'En almacén Natura', description: 'Tu pedido está siendo preparado en el almacén de Natura. En breve se lo daremos al transportista asignado a la entrega.' },
    { icon: '2-1', label: 'En almacén transportista', description: 'El transportista recibió tu pedido y está organizando la entrega.' },
    { icon: '3-1', label: 'En reparto', description: 'Tu pedido está en camino, lo recibirás en el transcurso del día.' },
    { icon: '3-2', label: 'En reparto fallido', description: 'No pudimos completar la entrega hoy. Ocurrió un imprevisto durante el recorrido y no fue posible entregar tu pedido en esta visita. Revisá el detalle del seguimiento para ver si hay instrucciones o aguardá la reprogramación del envío.' },
    { icon: '4-3', label: 'Entrega', description: 'Tu pedido fue entregado.' }
]

const WhatsAppCta = ({ text }: { text: string }) => {
    if (!DEFAULT.project.chat) {
        return null
    }
    return (
        <WhatsAppCtaStyled onClick={() => window.open(DEFAULT.project.chat)}>
            <WhatsAppCtaIconStyled />
            <Typography variant="body2">
                {text}
            </Typography>
        </WhatsAppCtaStyled>
    )
}

interface FaqItem {
    question: string,
    answer: ReactNode
}

const FAQS: Array<FaqItem> = [
    {
        question: '¿Qué estados puede tener mi pedido?',
        answer: (
            <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    A medida que tu pedido avanza, el estado se irá actualizando. A continuación, te explicamos qué significa cada uno.
                </Typography>
                <Box>
                    {ORDER_STATUSES.map((status) => (
                        <StatusRowStyled key={status.label}>
                            <StatusIconWrapperStyled>
                                <StepIconEvent icon={status.icon} />
                            </StatusIconWrapperStyled>
                            <Box>
                                <Typography variant="subtitle2">{status.label}</Typography>
                                <Typography variant="body2" color="textSecondary">{status.description}</Typography>
                            </Box>
                        </StatusRowStyled>
                    ))}
                </Box>
            </>
        )
    },
    {
        question: '¿Dónde veo la fecha estimada, el tipo de entrega y el transportista?',
        answer: (
            <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    En la parte superior del seguimiento, junto al número de pedido, vas a encontrar la fecha estimada de entrega y el tipo de entrega, que puede ser:
                </Typography>
                <ListStyled>
                    <li><Typography variant="body2" color="textSecondary">Entrega a domicilio</Typography></li>
                    <li><Typography variant="body2" color="textSecondary">Entrega en sucursal</Typography></li>
                </ListStyled>
                <Typography variant="body2" color="textSecondary">
                    Más abajo, dentro de los datos del pedido, vas a ver el nombre del transportista encargado de realizar la entrega.
                </Typography>
            </>
        )
    },
    {
        question: '¿Cómo sé cuándo me visita mi transportista asignado?',
        answer: (
            <Typography variant="body2" color="textSecondary">
                La fecha de entrega es estimada y podríamos visitarte antes. Te notificaremos por email, SMS, WhatsApp o llamada (según la empresa de transporte). Iremos a tu domicilio el día que tu pedido figure <strong>&quot;En reparto&quot;</strong>.
            </Typography>
        )
    },
    {
        question: '¿Qué pasa si mi pedido tiene más de una caja y cómo sigo cada una?',
        answer: (
            <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Algunos pedidos pueden dividirse en más de una caja para facilitar su traslado y entrega. Cuando tu pedido llegue al estado <strong>&quot;En almacén transportista&quot;</strong>, podrás:
                </Typography>
                <ListStyled>
                    <li><Typography variant="body2" color="textSecondary">Ver cuántas cajas componen el envío.</Typography></li>
                    <li><Typography variant="body2" color="textSecondary">Consultar el seguimiento y el estado de cada caja por separado.</Typography></li>
                </ListStyled>
                <ScreenshotStyled src={resource('images/faqs-3-0.png')} alt="Pedido con más de una caja: se muestra la cantidad de cajas del envío" />
                <ScreenshotStyled src={resource('images/faqs-3-1.png')} alt="Detalle del estado de cada caja por separado" />
                {/* <WhatsAppCta text="¿Necesitás ayuda con el seguimiento de tus cajas? Escribinos por WhatsApp" /> */}
            </>
        )
    },
    {
        question: '¿Qué pasa si mis cajas tienen estados diferentes?',
        answer: (
            <Typography variant="body2" color="textSecondary">
                En nuestro almacén y el del transportista trabajamos para garantizar que tus cajas viajen juntas. Si observás que tienen distintos estados es porque lamentablemente se han separado, pero no te preocupes, la entrega se realizará de todas formas.
            </Typography>
        )
    },
    {
        question: '¿Qué significa que mi pedido fue parcialmente entregado?',
        answer: (
            <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Significa que una o más cajas de tu pedido ya fueron entregadas, pero todavía queda al menos otra caja en proceso.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Podés ingresar al detalle del pedido para consultar el estado de cada caja por separado. La fecha de entrega final será la de la entrega de la última caja.
                </Typography>
            </>
        )
    },
    {
        question: '¿Qué hago si no pudieron entregar mi pedido?',
        answer: (
            <Typography variant="body2" color="textSecondary">
                En el webtracking verás el motivo si no pudimos entregar tu pedido. Si no estabas (ausente) o hubo un factor externo (clima), volveremos a pasar dentro de las 24-48 hs hábiles. Si hay un error en tu dirección, te contactaremos para solucionarlo.
            </Typography>
        )
    },
    {
        question: '¿Cómo hago una consulta o un reclamo?',
        answer: (
            <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    En la parte inferior derecha de la página vas a encontrar el acceso al chat de WhatsApp.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Desde allí podés realizar una consulta, solicitud o reclamo. Si generás un formulario de reclamo, lo recibiremos y lo contestaremos dentro de las 24 hs hábiles.
                </Typography>
            </>
        )
    }
]

const FaqsAr = () => {
    return (
        <RootStyled>
            <Typography variant="h5" color="primary" fontWeight={700} gutterBottom>
                Preguntas frecuentes
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Consultá el estado de tu pedido en cada etapa del envío. En esta guía te explicamos qué significa cada estado, cómo seguir las cajas de tu pedido y dónde comunicarte si necesitás ayuda.
            </Typography>
            {FAQS.map((faq, index) => (
                <Accordion key={faq.question} disableGutters TransitionProps={{ unmountOnExit: true }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" fontWeight={600}>{index + 1}. {faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {faq.answer}
                    </AccordionDetails>
                </Accordion>
            ))}
        </RootStyled>
    )
}

export default FaqsAr
